import { Spacing, Text } from '@vkontakte/vkui';
import React, { type VFC, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { platform } from 'reactApp/appHelpers/configHelpers';
import { overquota256TariffId } from 'reactApp/appHelpers/featuresHelpers/features/overquotaAbClearDownload';
import { QUOTA_PAGE_BASE_URL } from 'reactApp/constants/magicIdentificators';
import { getPaySource } from 'reactApp/modules/payment/payment.analytics';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { historyPush } from 'reactApp/modules/router/router.module';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { getOverQuota } from 'reactApp/modules/userQuota/userQuota.selectors';
import type { RootState } from 'reactApp/store';
import { CancelIcon } from 'reactApp/ui//VKUIIcons';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { sendDwh, sendXray } from 'reactApp/utils/ga';

import styles from './DownloadDisabledTooltip.css';

const sendRadar = (action: 'show' | 'click' | 'close' = 'show') => {
    sendXray(['overq_dwnld_tip', action]);

    sendDwh({
        eventCategory: 'tooltip',
        action,
        dwhData: {
            name: 'mnz_overquota_cleaner',
            platform,
        },
        sendImmediately: true,
    });
};

interface IProps {
    onClose(): void;
}

export const DownloadDisabledTooltip: VFC<IProps> = ({ onClose }) => {
    const dispatch = useDispatch();
    const quota = useSelector(getOverQuota);
    const storage = useSelector(getCurrentStorage);
    const product = useSelector((state: RootState) => ProductsSelectors.getProductById(state, overquota256TariffId));

    const handleClose = useCallback(() => {
        sendRadar('close');
        onClose();
    }, []);

    const handleClick = useCallback(() => {
        sendRadar('click');

        if (!product) {
            dispatch(historyPush({ id: QUOTA_PAGE_BASE_URL }));
            return;
        }

        dispatch(
            openTariffBuy({
                productId: product.id,
                onSuccess: () => location.reload(),
                isQuotaCleaner: true,
                paySource: getPaySource('tooltip_overquota_cleaner'),
            })
        );

        onClose();
    }, [product]);

    useEffect(sendRadar, []);

    useEffect(() => {
        if (storage !== EStorageType.quotaCleaner) {
            onClose();
        }
    }, [storage, onClose]);

    return (
        <div className={styles.root} data-prevent-datalist-reset>
            <div className={styles.closeButton} onClick={handleClose}>
                <CancelIcon />
            </div>
            <Text className={styles.title}>Скачать файлы пока нельзя</Text>
            <Spacing size={8} />
            <Text className={styles.text}>
                Облако переполнено на <span className={styles.space}>{quota?.value}</span>. Увеличьте место с подпиской Mail Space или
                удалите ненужные файлы
            </Text>
            <Spacing size={24} />
            <Button sizeMode={ButtonSizeMode.small} theme="octavius" primary onClick={handleClick} className={styles.button}>
                <Text className={styles.buttonText}>Увеличить место</Text>
            </Button>
        </div>
    );
};
